import React from 'react';

// Short viewports (phones in landscape) and panes that would cover most of
// the screen keep normal scrolling.
const MIN_VIEWPORT_HEIGHT = 600;
const MAX_VIEWPORT_SHARE = 0.4;

export interface IUseFrozenPaneResult {
  ref: (node: HTMLDivElement | null) => void;
  frozen: boolean;
  height: number;
}

/** @registry Measures a FrozenPane (border box) and reports whether it should pin: viewport at least 600px tall and the pane no taller than 40% of it. */
export const useFrozenPane = (): IUseFrozenPaneResult => {
  // A callback ref, so a pane that mounts after the owning component (behind
  // a loading state or a tab) is still observed.
  const [node, setNode] = React.useState<HTMLDivElement | null>(null);
  const [height, setHeight] = React.useState(0);
  // A media query rather than a viewport-size subscription, so the owning
  // page re-renders only when the answer flips, not on every resize event.
  const query = `(min-height: ${Math.max(
    MIN_VIEWPORT_HEIGHT,
    height / MAX_VIEWPORT_SHARE,
  )}px)`;
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      const media = window.matchMedia(query);
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    },
    [query],
  );
  const viewportFits = React.useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
  );

  React.useEffect(() => {
    if (!node) {
      setHeight(0);
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setHeight(
        entry.borderBoxSize?.[0]?.blockSize ??
          entry.target.getBoundingClientRect().height,
      );
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { ref: setNode, frozen: height > 0 && viewportFits, height };
};
